import { useEffect, useRef, useState } from "react";

import { GENERIC_PHOTO_SLOTS } from "../../api/images";
import { checkFile, formatBytes } from "../useImageReplacement";

/**
 * The photographs of a product that is photographed the ordinary way.
 *
 * ONE uploader serves Contact Lenses and Accessories because their source
 * contract is genuinely the same — one photograph required, two more optional —
 * and two components with that same contract would be two places for it to
 * drift. What differs between those families is the commerce fields beside
 * this, which is why those live elsewhere and this asks only about photographs.
 *
 * It is deliberately not the glasses control either. A frame is described by
 * three named views the pipeline composes a card from, and its intake reads a
 * folder to find out which file is which. Here the administrator says which
 * photograph is the main one by putting it in the first slot, so there is
 * nothing to detect and no folder to parse.
 *
 * Nothing is cropped, resized or re-encoded here. The bytes chosen are the
 * bytes uploaded; the backend owns every derivative made from them.
 */

/** One chosen photograph, keyed by the source role it will be sent under. */
export type PhotoSelection = Record<string, File | undefined>;

export function ProductPhotosStep({
  value,
  onChange,
  disabled = false,
  variantName,
  groupKey,
}: {
  value: PhotoSelection;
  onChange: (next: PhotoSelection) => void;
  disabled?: boolean;
  /** The colour these photographs are OF, when the product has more than one.
   *
   *  A photograph of the silver chain is not a photograph of the gold one, so
   *  each colour is photographed in its own group and the group says whose it
   *  is. Absent for a product with a single variant, where there is no colour
   *  to name and the plain three slots are the whole question. */
  variantName?: string;
  /** Distinguishes one colour's slots from another's. Absent on a
   *  single-variant product, where the slots keep the plain names they have
   *  always had. */
  groupKey?: string;
}) {
  const slotId = (role: string) =>
    groupKey ? `${groupKey}-${role}` : role;
  return (
    <section
      className="step"
      aria-label={
        variantName ? `Photographs of ${variantName}` : "Product photographs"
      }
    >
      {variantName ? (
        <h3 className="section-title">{variantName}</h3>
      ) : null}
      <p className="steps__hint">
        The first photograph is the one customers see on the product card. The
        other two are optional — a product with one photograph is complete.
      </p>
      <div className="photoSlots">
        {GENERIC_PHOTO_SLOTS.map((slot) => (
          <PhotoSlot
            key={slot.role}
            slotId={slotId(slot.role)}
            label={slot.label}
            // Two colours both have a "Main photograph"; the accessible name
            // has to say which one, or the second is unreachable by name.
            ariaLabel={
              variantName ? `${slot.label} for ${variantName}` : slot.label
            }
            hint={slot.hint}
            required={slot.required}
            file={value[slot.role]}
            disabled={disabled}
            onPick={(file) => onChange({ ...value, [slot.role]: file })}
            onClear={() => {
              const next = { ...value };
              delete next[slot.role];
              onChange(next);
            }}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * A viewable URL for a chosen file, for as long as it is the chosen file.
 *
 * The browser will show a local file without anyone uploading it, and that is
 * the whole point here: an administrator has to be able to see that the picture
 * they picked is the picture they meant BEFORE it is sent. Waiting for the
 * upload to answer would make the check useless — by then it is on the product.
 *
 * The URL is revoked when the file changes and when the slot unmounts, which is
 * what keeps a long session from accumulating one blob per file the operator
 * ever browsed past. The effect's cleanup is the only place that revokes, so
 * there is exactly one lifetime rule rather than one per caller.
 *
 * Nothing is copied, re-encoded or converted to a data URL. The File is the
 * source, and the bytes uploaded later are the same bytes.
 */
function useObjectUrl(file: File | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    // Guarded because a jsdom test environment has no object URLs unless the
    // test provides them; a slot without a preview is still a usable slot, and
    // an exception here would take the whole intake down with it.
    if (typeof URL.createObjectURL !== "function") return;
    const created = URL.createObjectURL(file);
    setUrl(created);
    return () => {
      setUrl(null);
      URL.revokeObjectURL(created);
    };
  }, [file]);

  return url;
}

function PhotoSlot({
  slotId,
  label,
  ariaLabel,
  hint,
  required,
  file,
  disabled,
  onPick,
  onClear,
}: {
  /** The role, prefixed by the colour's group when there is more than one. */
  slotId: string;
  label: string;
  ariaLabel: string;
  hint: string;
  required: boolean;
  file: File | undefined;
  disabled: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Checked before anything is sent, so an obviously unusable file is reported
  // here rather than as a rejection after an upload the administrator waited
  // for. The backend stays authoritative and checks again.
  const problem = file ? checkFile(file) : null;
  const previewUrl = useObjectUrl(file);

  return (
    <div className="photoSlot" data-testid={`photo-slot-${slotId}`}>
      <div className="photoSlot__head">
        <strong>{label}</strong>
        <span className="muted">{required ? "Required" : "Optional"}</span>
      </div>
      <p className="field__hint">{hint}</p>

      <input
        ref={inputRef}
        className="visuallyHidden"
        type="file"
        accept="image/avif,image/webp,image/png,image/jpeg"
        aria-label={ariaLabel}
        data-testid={`photo-input-${slotId}`}
        disabled={disabled}
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          if (chosen) onPick(chosen);
          // Cleared so choosing the same file twice still fires a change.
          event.target.value = "";
        }}
      />

      {file ? (
        <div className="photoSlot__chosen">
          {/* The photograph itself, and it stays here for the rest of the
              intake: while it uploads, and after it has. Collapsing a finished
              slot to "photo_1 done" would take away the only thing that shows
              WHICH photograph finished. */}
          {previewUrl ? (
            <img
              className="photoSlot__preview"
              src={previewUrl}
              // Named after the slot, so a screen reader hears which of the
              // three — and on a multi-colour product, whose — this is.
              alt={`Selected ${ariaLabel}`}
              data-testid={`photo-preview-${slotId}`}
            />
          ) : null}
          <span className="photoSlot__name">{file.name}</span>
          <span className="muted num">{formatBytes(file.size)}</span>
          <div className="row">
            <button
              type="button"
              className="btn btn--sm btn--subtle"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              Change
            </button>
            <button
              type="button"
              className="btn btn--sm btn--subtle"
              disabled={disabled}
              aria-label={`Remove ${ariaLabel}`}
              onClick={onClear}
            >
              Remove
            </button>
          </div>
          {problem ? <p className="field-error">{problem}</p> : null}
        </div>
      ) : (
        <button
          type="button"
          className="btn btn--sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Choose photograph
        </button>
      )}
    </div>
  );
}

/**
 * The photographs in slot order, as the intake will send them.
 *
 * Order is the slot order, never the order they were chosen in: `photo_2` is
 * the second photograph because it is in the second slot. An absent optional
 * slot is skipped rather than filled — duplicating the main photograph to fill
 * `photo_2` would put the same picture on the product twice and claim it was a
 * second view.
 */
export function photosInSlotOrder(
  value: PhotoSelection,
): { role: string; label: string; file: File }[] {
  return GENERIC_PHOTO_SLOTS.flatMap((slot) => {
    const file = value[slot.role];
    return file ? [{ role: slot.role, label: slot.label, file }] : [];
  });
}

/** What is still wrong with the chosen photographs, in the operator's words. */
export function photoProblems(value: PhotoSelection): string[] {
  const problems: string[] = [];
  for (const slot of GENERIC_PHOTO_SLOTS) {
    const file = value[slot.role];
    if (!file) {
      if (slot.required) problems.push(`${slot.label} is required.`);
      continue;
    }
    const problem = checkFile(file);
    if (problem) problems.push(`${slot.label}: ${problem}`);
  }
  return problems;
}
